import { Suspense, useEffect, useState } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@material-tailwind/react';
import { App as AntdApp, ConfigProvider, theme } from 'antd';
import enUS from 'antd/locale/en_US';

import store from './redux/store';
import AppContent from './AppContent';
import Loading from './components/UI/loading/Loading';
import { fetchUser } from './redux/auth/actions';
import {
  selectCurrentAdmin,
  selectPlatformRoles
} from './redux/auth/selectors';
import { fetchTree } from './redux/context/actions';
import { selectTree } from './redux/context/selectors';
import { 
  accessShapeOf, 
  consolePathFor, 
  indexTree 
} from './redux/context/treeUtils';
import { switchToWorkspace } from './redux/context/navigation';
import { logout } from './service/auth';
import {
  purgeLegacyTableState
} from './components/UI/table/tableStatePersistence';

function AppInitializer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const current = useSelector(selectCurrentAdmin);
  const [initializing, setInitializing] = useState(true);

  // Table views used to be stored per table name alone, with no user and no workspace in the
  // key. Those entries cannot be assigned to an owner now, so they go rather than follow the
  // wrong person into the wrong workspace. Every table falls back to its defaults once.
  useEffect(() => {
    purgeLegacyTableState();
  }, []);

  useEffect(() => {
    // Where a fresh sign-in starts: the saved home, else what the access shape implies.
    // Returns false when nothing applies, so the generic fallback below still runs.
    const landFresh = async (updatedCurrent: any): Promise<boolean> => {
      try {
        await dispatch<any>(fetchTree());
      } catch {
        return false;
      }
      const roots: any[] = selectTree(store.getState()) ?? [];
      const index: any = indexTree(roots);
      const activeId = updatedCurrent?.default_workspace?.id;
      const openWorkspace = async (node: any) => {
        if (String(activeId) !== String(node.id)) await switchToWorkspace(dispatch, node);
      };

      const home = updatedCurrent?.home_node_id != null ? index[updatedCurrent.home_node_id] : null;
      if (home) {
        if (home.kind === 'workspace') {
          await openWorkspace(home);
          return true;
        }
        const path = consolePathFor(home);
        if (path) {
          navigate(path, { replace: true });
          return true;
        }
      }

      const { shape } = accessShapeOf(roots);
      if (shape === 'org') {
        const org = roots.find((n) => n.kind === 'organisation');
        const path = org && consolePathFor(org);
        if (path) {
          navigate(path, { replace: true });
          return true;
        }
      } else if (shape === 'entities') {
        const entities = roots
          .filter((n) => n.kind === 'entity')
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        const path = entities[0] && consolePathFor(entities[0]);
        if (path) {
          navigate(path, { replace: true });
          return true;
        }
      } else if (activeId == null) {
        navigate('/workspaces', { replace: true });
        return true;
      }
      return false;
    };

    const checkAuth = async () => {
      const { pathname } = window.location;

      // Tokens from pre-OIDC builds were persisted indefinitely. The current
      // session is intentionally tab-scoped and Keycloak can restore SSO after
      // a new login redirect, so it is safe to discard the legacy copy.
      localStorage.removeItem('auth');

      // Public pages an unauthenticated visitor must be able to reach without
      // being redirected to /login (e.g. invitees opening an email link fresh).
      //
      // `/licence` above all: a 402 from the licence gate clears the session and loads it as
      // a full page, so without it here the trial-ended screen bounces to /login before it can
      // render, and a client whose trial has ended has nowhere to paste the key.
      const PUBLIC_PREFIXES = [
        '/auth/', '/accept-invitation', '/invitations/', '/licence', '/_internal/knowledge-v2'
      ];
      const isPublicPath = PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));

      // Set by OidcCallback, read once: only the first load after signing in may redirect.
      const freshSignIn = sessionStorage.getItem('oidc:freshSignIn') === '1';
      sessionStorage.removeItem('oidc:freshSignIn');

      if (!isPublicPath) {
        const auth = sessionStorage.getItem('auth');
        if (auth) {
          await dispatch<any>(fetchUser());

          // Important: Refetch current after dispatch
          const updatedCurrent: any = store.getState().auth.current;
          const platformRoles: string[] = selectPlatformRoles(store.getState());
          const platformAccount = platformRoles.includes('PLATFORM_OWNER')
            || platformRoles.includes('SUPPORT_ADMIN');
          const onPlatformPage = pathname.startsWith('/admin');
          if (platformAccount && freshSignIn && !onPlatformPage) {
            // A platform account starts in the console, not in the workspace it used last.
            navigate('/admin', { replace: true });
          } else if (freshSignIn && !platformAccount && await landFresh(updatedCurrent)) {
            // Landed by the home / access-shape rules.
          } else if (!updatedCurrent?.default_workspace?.id && !(platformAccount && onPlatformPage)) {
            // Only redirect if workspace is not set. The console needs none.
            let target = '/workspaces';
            try {
              await dispatch<any>(fetchTree());
              const root = (selectTree(store.getState()) ?? []).find((n: any) => n.has_console);
              target = (root && consolePathFor(root)) || target;
            } catch {
              // A failed tree load must not strand the user on this page.
            }
            navigate(target);
          }
        } else {
          logout();
          navigate('/login');
          console.warn('User not authenticated, redirecting to login.');
        }
      }

      setInitializing(false);
    };

    checkAuth();
    // navigate is left out deliberately. This is the auth bootstrap: running it a second
    // time re-fetches the user and can log the session out mid-session, so it belongs on
    // mount only, whatever react-router does with the identity of navigate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  if (initializing) return <Loading />;
  return <AppContent />;
}

function App() {
  return (
    <Provider store={store}>
      {/*
        locale is not only for antd's own copy. @ant-design/pro-provider reads it out of
        this context and calls the GLOBAL dayjs.locale() with it, falling back to 'zh-cn'
        when it finds nothing — so a Pro component mounted outside an antd locale switches
        every date in the app to Chinese. Setting it at the root means there is no such
        place. See the nesting note in DefaultLayout.
      */}
      <ConfigProvider
        locale={enUS}
        theme={{ algorithm: theme.compactAlgorithm }}
      >
        <AntdApp>
          <ThemeProvider>
            <AppInitializer />
          </ThemeProvider>
        </AntdApp>
      </ConfigProvider>
    </Provider>
  );
}

export default App;
